/**
 * Date Formatting Utilities for Event Timestamps
 * 
 * This module provides utilities for formatting event dates that are stored as
 * UTC timestamps (ISO 8601 format) and displayed in their event-specific timezone.
 * 
 * All functions handle:
 * - ISO 8601 UTC timestamps from the API
 * - IANA timezone identifiers (e.g., "America/Los_Angeles", "Europe/London")
 * - Browser compatibility (Safari, Chrome, Firefox, Edge)
 * - Automatic DST (Daylight Saving Time) handling
 */

/**
 * Format an event date in the event's timezone
 * 
 * @param {string} isoDateString - ISO 8601 UTC date string (e.g., "2025-07-15T21:00:00.000Z")
 * @param {string} eventTimezone - IANA timezone identifier (e.g., "America/Los_Angeles")
 * @param {object} options - Optional formatting options
 * @param {boolean} options.includeTimezone - Whether to include timezone abbreviation (default: true)
 * @param {string} options.locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted date string (e.g., "July 15, 2025, 2:00 PM PDT")
 * 
 * @example
 * formatEventDate("2025-07-15T21:00:00.000Z", "America/Los_Angeles")
 * // Returns: "July 15, 2025, 2:00 PM PDT"
 * 
 * @example
 * formatEventDate("2025-07-15T21:00:00.000Z", "America/New_York", { includeTimezone: false })
 * // Returns: "July 15, 2025, 5:00 PM"
 */
export const formatEventDate = (isoDateString, eventTimezone, options = {}) => {
  if (!isoDateString) return 'Date unavailable';
  
  const {
    includeTimezone = true,
    locale = 'en-US'
  } = options;
  
  try {
    const date = new Date(isoDateString);
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', isoDateString);
      return 'Invalid date';
    }
    
    const formatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: eventTimezone || 'America/Los_Angeles'
    };
    
    if (includeTimezone) {
      formatOptions.timeZoneName = 'short';
    }
    
    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error, { isoDateString, eventTimezone });
    return 'Date unavailable';
  }
};

/**
 * Convert ISO 8601 UTC string to datetime-local input format
 * This is used for populating datetime-local input fields in forms
 * 
 * @param {string} isoDateString - ISO 8601 UTC date string
 * @param {string} eventTimezone - IANA timezone identifier
 * @returns {string} datetime-local format (YYYY-MM-DDTHH:mm)
 * 
 * @example
 * isoToDatetimeLocal("2025-07-15T21:00:00.000Z", "America/Los_Angeles")
 * // Returns: "2025-07-15T14:00" (converted to Pacific time)
 */
export const isoToDatetimeLocal = (isoDateString, eventTimezone) => {
  if (!isoDateString) return '';
  
  try {
    const date = new Date(isoDateString);
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date for datetime-local conversion:', isoDateString);
      return '';
    }
    
    // Use Swedish locale ('sv-SE') which naturally formats as YYYY-MM-DD HH:mm:ss
    // This is a reliable cross-browser way to get ISO-like formatting
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: eventTimezone || 'America/Los_Angeles',
      hour12: false
    });
    
    const formatted = formatter.format(date);
    // Format is "YYYY-MM-DD HH:mm:ss", we need "YYYY-MM-DDTHH:mm"
    return formatted.substring(0, 16).replace(' ', 'T');
  } catch (error) {
    console.error('Error converting to datetime-local:', error);
    return '';
  }
};

/**
 * Convert datetime-local input value to ISO 8601 UTC string
 * This is used when submitting forms with datetime-local inputs
 * 
 * @param {string} datetimeLocal - datetime-local format (YYYY-MM-DDTHH:mm)
 * @param {string} eventTimezone - IANA timezone identifier
 * @returns {string} ISO 8601 UTC string
 * 
 * @example
 * datetimeLocalToISO("2025-07-15T14:00", "America/Los_Angeles")
 * // Returns: "2025-07-15T21:00:00.000Z" (converted to UTC)
 */
export const datetimeLocalToISO = (datetimeLocal, eventTimezone) => {
  if (!datetimeLocal) return '';
  
  try {
    // Parse the datetime-local string
    // Format is "YYYY-MM-DDTHH:mm"
    const [datePart, timePart] = datetimeLocal.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    
    // Create a date string in the event's timezone
    // We'll use the Intl API to properly handle timezone conversion
    const dateInEventTz = new Date(year, month - 1, day, hour, minute, 0);
    
    // Get the UTC offset for the event timezone at this specific date/time
    // This properly handles DST
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone || 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Format the date in the event timezone
    const parts = formatter.formatToParts(dateInEventTz);
    const getValue = (type) => parts.find(p => p.type === type)?.value;
    
    // Create a new date with the correct timezone interpretation
    const tzYear = parseInt(getValue('year'));
    const tzMonth = parseInt(getValue('month')) - 1;
    const tzDay = parseInt(getValue('day'));
    const tzHour = parseInt(getValue('hour'));
    const tzMinute = parseInt(getValue('minute'));
    
    // Calculate the offset
    const localDate = new Date(year, month - 1, day, hour, minute, 0);
    const tzDate = new Date(tzYear, tzMonth, tzDay, tzHour, tzMinute, 0);
    const offset = tzDate.getTime() - localDate.getTime();
    
    // Apply offset to get UTC
    const utcDate = new Date(localDate.getTime() - offset);
    
    return utcDate.toISOString();
  } catch (error) {
    console.error('Error converting datetime-local to ISO:', error);
    // Fallback: treat as local time and convert
    try {
      const date = new Date(datetimeLocal);
      return date.toISOString();
    } catch (fallbackError) {
      console.error('Fallback conversion also failed:', fallbackError);
      return '';
    }
  }
};

/**
 * Get a list of common timezones for timezone selector dropdowns
 * 
 * @returns {Array<{value: string, label: string}>} Array of timezone options
 */
export const getCommonTimezones = () => [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' }
];


/**
 * Parse a calendar date (YYYY-MM-DD) as that date, in the viewer's own timezone.
 *
 * `new Date('2026-09-01')` is specified to parse as UTC midnight, and every render after that
 * converts to local time. West of Greenwich the clock has not reached the 1st yet, so the date
 * comes back as 31 August: a September iteration filed under August for every admin in the US,
 * while the same screen read correctly from India. The values these come from — periodStart,
 * periodEnd — are calendar boundaries, not instants, and must never be put through a timezone
 * conversion.
 *
 * Datetimes (anything carrying a time or a Z) are instants and are left to the normal parser.
 */
export const parseLocalDate = (value) => {
  if (!value) return null;
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return new Date(text);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

/**
 * The calendar day an instant falls on in a timezone, as YYYY-MM-DD.
 */
const dayIn = (date, timeZone) => new Intl.DateTimeFormat('en-CA', {
  timeZone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}).format(date);

/**
 * How many days before an event's first day its tickets can already be scanned. Matches the
 * server's app.tickets.scan-days-before.
 */
export const SCAN_DAYS_BEFORE = 2;

/** YYYY-MM-DD, `days` calendar days earlier. Calendar arithmetic, so DST cannot shift it. */
const daysEarlier = (day, days) => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date - days)).toISOString().slice(0, 10);
};

/**
 * DV-334: whether an event's tickets can be scanned today, in its own timezone — from midnight
 * SCAN_DAYS_BEFORE days before its first day to the end of its last day. The buffer is only before
 * the event. The server applies the same rule to every scan; this keeps events it would refuse out
 * of the picker.
 *
 * @param {{startDate: string, endDate: string, timezone?: string}} event - dates as ISO 8601 UTC
 * @param {Date} now
 * @param {number} daysBefore
 * @returns {boolean}
 */
export const isEventScannableToday = (event, now = new Date(), daysBefore = SCAN_DAYS_BEFORE) => {
  if (!event || !event.startDate || !event.endDate) return false;
  const timeZone = event.timezone || 'America/Los_Angeles';
  try {
    const today = dayIn(now, timeZone);
    const opens = daysEarlier(dayIn(new Date(event.startDate), timeZone), daysBefore);
    return opens <= today && today <= dayIn(new Date(event.endDate), timeZone);
  } catch (error) {
    console.error('Error reading event dates:', error);
    return false;
  }
};
